import { SecureLinkEntityType } from '@aiwyn/auth';
import { BaseCommonClientRequest } from '@aiwyn/common/client-requests';
import { ClientRequestDocumentUploadModel, Engagement, EngagementStep, EngagementStepType } from '@aiwyn/api';

export interface SecureLinkClientRequest {
  clientRequestItemIds?: number[],
  request: BaseCommonClientRequest<ClientRequestDocumentUploadModel>;
}

export interface SecureLinkEngagementStep extends EngagementStep {
  engagementId: number;
  documentContactId: number;
}

/**
 * Type map to link entity enums to entity interfaces
 */
export type SecureLinkEntityTypeToEntityMap<EntityType extends SecureLinkEntityType> =
  EntityType extends SecureLinkEntityType.CLIENT_REQUEST ?
    SecureLinkClientRequest :
    EntityType extends SecureLinkEntityType.ENGAGEMENT_STEP ?
      SecureLinkEngagementStep :
      never;

export enum SecureLinkEntityStatus {
  IN_PROGRESS = 'In Progress',
  COMPLETED = 'Completed',
  REJECTED = 'Rejected',
}

interface BaseGrantEntity<EntityType extends SecureLinkEntityType = SecureLinkEntityType> {
  entityId: number;
  entityIdentity: SecureLinkEntityIdentity<EntityType>; // required since some entities (eg: EngagementStep) don't have a unique ID
  entityType: EntityType;
  entityName: string;
  entityStatus: SecureLinkEntityStatus;
  entity: SecureLinkEntityTypeToEntityMap<EntityType>;

  clientId: number;
}

export type GrantEntity<EntityType extends SecureLinkEntityType = SecureLinkEntityType> =
  BaseGrantEntity<EntityType>
  & EntityToExtendedModelMap<EntityType>;

type EntityToExtendedModelMap<Entity extends SecureLinkEntityType> =
  Entity extends SecureLinkEntityType.ENGAGEMENT_STEP
    ? object
    : { jobId: number; };

export interface SecureLinkEngagement extends Engagement {
  statusMessage: string;
}

/**
 * Examples of valid and invalid `SecureLinkEntityIdentity` types:
 *
 * Valid:
 * - ClientRequest-123
 * - EngagementStep-456-LETTER
 *
 * Invalid:
 * - ClientRequest
 * - ClientRequest-123-EXTRA (extra part after number, invalid suffix based on entity type)
 * - EngagementStep-ABC-SOME_STEP_TYPE (number is expected after the first dash)
 * - EngagementStep-123-DANCE ('DANCE' doesn’t match a valid EngagementStepType)
 * - UNKNOWN_TYPE-789 ('UNKNOWN_TYPE' is not a valid SecureLinkEntityType)
 */
export type SecureLinkEntityIdentity<Type extends SecureLinkEntityType = SecureLinkEntityType> =
  `${Type}-${number}${SecureLinkEntityIdentitySuffix<Type>}`;

type SecureLinkEntityIdentitySuffix<Type extends SecureLinkEntityType> =
  Type extends SecureLinkEntityType.CLIENT_REQUEST
    ? ''
    : Type extends SecureLinkEntityType.ENGAGEMENT_STEP
      ? `-${EngagementStepType}`
      : never;

export interface EntityRequest<EntityType extends SecureLinkEntityType> {
  entityType: EntityType;
  entityIds: number[];
}

export interface ClientRequestEntityRequest extends EntityRequest<SecureLinkEntityType.CLIENT_REQUEST> {
  restrictedRequestItemsByRequestId?: Map<number, number[]>;
}

export interface EngagementStepEntityRequest extends EntityRequest<SecureLinkEntityType.ENGAGEMENT_STEP> {
  engagementMap?: Map<number, number[]>; // Map<EngagementId, DocumentContactId[]>
}
